import React, { useState, useEffect, useRef } from 'react';
import { Card, Form, Button, Alert, Spinner, Tabs, Tab, Badge, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faPlus, faBell, faUserGroup, faCheck, faTimes, faSignOutAlt } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { useUserContext } from 'layouts/UserContext';
import { getHatateToken } from 'helpers/utils';

type JoinGroupComponentProps = {
    language: 'es' | 'en';
    disabled?: boolean;
};

interface PendingInvitation {
    id: string;
    inviteCode: string;
    sender: string;
    timestamp: string;
    groupName?: string;
}

interface GroupInfo {
    id: string;
    name: string;
    participants: number;
    lastActivity: string;
    isActive: boolean;
}

const JoinGroupComponent: React.FC<JoinGroupComponentProps> = ({ language, disabled = false }) => {
    const {
        user
    } = useUserContext();
    
    // Estados para unirse a grupos
    const [inviteCode, setInviteCode] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    
    // Estados para invitaciones pendientes
    const [pendingInvitations, setPendingInvitations] = useState<PendingInvitation[]>([]);
    const [isLoadingInvitations, setIsLoadingInvitations] = useState(false);
    const [isProcessingInvitation, setIsProcessingInvitation] = useState(false);
    
    // Estados para listado de grupos
    const [groups, setGroups] = useState<GroupInfo[]>([]);
    const [isLoadingGroups, setIsLoadingGroups] = useState(false);
    const [isLeavingGroup, setIsLeavingGroup] = useState(false);
    
    // Estado para la pestaña actual
    const [activeTab, setActiveTab] = useState('join');
    
    // Referencias a los intervalos de actualización
    const invitationsIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const groupsIntervalRef = useRef<NodeJS.Timeout | null>(null);
    
    // API info
    const API_KEY = 'anubis-whatsapp-api-key';
    const API_TOKEN_KEY = getHatateToken();
    const API_BASE_URL = 'https://backend.anubisai.net/api/whatsapp/bot';
    const whatsAppServerPort = user.mlStuff.whatsAppServerPort;
    const whatsAppApiPort = user.mlStuff.whatsAppApiPort;
    const mlServerPort = user.mlStuff.mlServerPort;

    // Cargar datos iniciales
    useEffect(() => {
        // Iniciar intervalos de actualización silenciosa
        startBackgroundRefresh();
        
        // Limpiar intervalos al desmontar el componente
        return () => {
            clearBackgroundRefresh();
        };
    }, []);
    
    // Iniciar actualizaciones de fondo
    const startBackgroundRefresh = () => {
        // Actualizar invitaciones pendientes cada 15 segundos
        invitationsIntervalRef.current = setInterval(() => {
            loadPendingInvitations();
        }, 40000);
        
        // Actualizar grupos cada 90 segundos
        groupsIntervalRef.current = setInterval(() => {
            loadGroups();
        }, 190000);
    };
    
    // Limpiar intervalos de actualización
    const clearBackgroundRefresh = () => {
        if (invitationsIntervalRef.current) {
            clearInterval(invitationsIntervalRef.current);
            invitationsIntervalRef.current = null;
        }
        
        if (groupsIntervalRef.current) {
            clearInterval(groupsIntervalRef.current);
            groupsIntervalRef.current = null;
        }
    };

    // Cargar invitaciones pendientes
    const loadPendingInvitations = async () => {
        if (isLoadingInvitations) return; // Evitar cargas múltiples simultáneas
        
        setIsLoadingInvitations(true);
        
        try {
            const currentToken = getHatateToken();
            const response = await axios.get(
                `${API_BASE_URL}/pending-invitations`,
                { 
                    headers: { 
                        'x-api-key': API_KEY,
                        'Authorization': `${currentToken}`
                    },
                    params: {
                        mlStuff: {
                            whatsAppServerPort: whatsAppServerPort,
                            whatsAppApiPort: whatsAppApiPort,
                            mlServerPort: mlServerPort
                        }
                    }
                }
            );
            
            if (response.data.success) {
                setPendingInvitations(response.data.invitations || []);
            }
        } catch (err: any) {
            console.error('Error loading pending invitations:', err);
            // No mostrar error para las invitaciones pendientes, simplemente mostrar vacío
        } finally {
            setIsLoadingInvitations(false);
        }
    };

    // Cargar listado de grupos
const loadGroups = async () => {
    if (isLoadingGroups) return; // Avoid multiple simultaneous loads
    
    setIsLoadingGroups(true);
    
    try {
        // Get a fresh token for this API call
        const currentToken = getHatateToken();
        
        const response = await axios.get(
            `${API_BASE_URL}/groups`,
            { 
                headers: { 
                    'x-api-key': API_KEY,
                    'Authorization': `${currentToken}` // Use fresh token
                },
                params: {
                    mlStuff: {
                        whatsAppServerPort: whatsAppServerPort,
                        whatsAppApiPort: whatsAppApiPort,
                        mlServerPort: mlServerPort
                    }
                }
            }
        );
        
        if (response.data.success) {
            const currentTime = new Date().getTime();
            const fiveMinutesInMs = 5 * 60 * 1000;
            const groups = response.data.groups || [];
            
            // Create an array to store promises for fetching details
            const detailPromises = groups.map(async (group) => {
                try {
                    // Get a fresh token for this detail API call
                    const detailToken = getHatateToken();
                    
                    // Fetch conversation details to get accurate last message timestamp
                    const detailResponse = await axios.get(`${API_BASE_URL}/conversation-detail`, {
                        headers: {
                            'x-api-key': API_KEY,
                            'Authorization': `${detailToken}`
                        },
                        params: { 
                            id: group.id 
                        }
                    });
                    
                    if (detailResponse.data.success && 
                        detailResponse.data.messages && 
                        detailResponse.data.messages.length > 0) {
                        
                        // Get the timestamp of the most recent message
                        const messages = detailResponse.data.messages;
                        const lastMessageTimestamp = messages[messages.length - 1].timestamp;
                        
                        // Check if the group is active based on last message timestamp
                        const lastActivityTime = new Date(lastMessageTimestamp).getTime();
                        const isActive = (currentTime - lastActivityTime) <= fiveMinutesInMs;
                        
                        return {
                            ...group,
                            lastActivity: lastMessageTimestamp,
                            isActive
                        };
                    } else {
                        // If no messages are found, fall back to the original lastActivity
                        const lastActivityTime = new Date(group.lastActivity).getTime();
                        const isActive = (currentTime - lastActivityTime) <= fiveMinutesInMs;
                        
                        return {
                            ...group,
                            isActive
                        };
                    }
                } catch (error) {
                    console.warn(`Error fetching details for group ${group.id}:`, error);
                    // Return the original group data if detail fetch fails
                    const lastActivityTime = new Date(group.lastActivity).getTime();
                    const isActive = (currentTime - lastActivityTime) <= fiveMinutesInMs;
                    
                    return {
                        ...group,
                        isActive
                    };
                }
            });
            
            // Wait for all detail fetches to complete
            const processedGroups = await Promise.all(detailPromises);
            setGroups(processedGroups);
        }
    } catch (err) {
        console.error('Error loading groups:', err);
        // Don't show error for groups, just show empty
    } finally {
        setIsLoadingGroups(false);
    }
};


    // Manejar unirse a grupo manualmente
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!inviteCode.trim()) {
            setError(language === 'es' 
                ? 'Por favor introduce un código de invitación válido' 
                : 'Please enter a valid invitation code');
            return;
        }
        
        setIsSubmitting(true);
        setError(null);
        setSuccess(null);
        
        try {
            // Normalizar el código de invitación (eliminar espacios)
            let cleanCode = inviteCode.trim();
            
            // Si es una URL completa, extraer solo el código
            if (cleanCode.includes('chat.whatsapp.com/')) {
                cleanCode = cleanCode.split('chat.whatsapp.com/')[1];
            }

            const currentToken = getHatateToken();
            const response = await axios.post(
                `${API_BASE_URL}/join-group`,
                { inviteCode: cleanCode },
                { 
                    headers: { 
                        'x-api-key': API_KEY,
                        'Authorization': `${currentToken}`
                    },
                    params: {
                        mlStuff: {
                            whatsAppServerPort: whatsAppServerPort,
                            whatsAppApiPort: whatsAppApiPort,
                            mlServerPort: mlServerPort
                        }
                    }
                }
            );
            
            if (response.data.success) {
                setSuccess(language === 'es'
                    ? 'Bot unido exitosamente al grupo'
                    : 'Bot successfully joined the group');
                
                // Limpiar el formulario después de un éxito
                setInviteCode('');
                
                // Recargar listado de grupos
                loadGroups();
                
                // Ocultar mensaje de éxito después de 5 segundos
                setTimeout(() => {
                    setSuccess(null);
                }, 5000);
            } else {
                setError(response.data.message || (language === 'es'
                    ? 'Error al unirse al grupo'
                    : 'Error joining the group'));
            }
        } catch (err: any) {
            console.error('Error joining group:', err);
            
            // Extraer mensaje de error de la respuesta de axios si existe
            const errMessage = err.response?.data?.message 
                || err.message 
                || (language === 'es' ? 'Error desconocido' : 'Unknown error');
            
            setError(language === 'es'
                ? `Error al unirse al grupo: ${errMessage}`
                : `Error joining group: ${errMessage}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    // Procesar invitación (aprobar o rechazar)
    const handleProcessInvitation = async (invitationId: string, approve: boolean) => {
        setIsProcessingInvitation(true);
        setError(null);
        setSuccess(null);
        
        try {
            const currentToken = getHatateToken();
            const response = await axios.post(
                `${API_BASE_URL}/process-invitation`,
                { 
                    invitationId,
                    approve 
                },
                { 
                    headers: { 
                        'x-api-key': API_KEY,
                        'Authorization': `${currentToken}`
                    },
                    params: {
                        mlStuff: {
                            whatsAppServerPort: whatsAppServerPort,
                            whatsAppApiPort: whatsAppApiPort,
                            mlServerPort: mlServerPort
                        }
                    }
                }
            );
            
            if (response.data.success) {
                setSuccess(approve 
                    ? (language === 'es' ? 'Invitación aprobada correctamente' : 'Invitation approved successfully')
                    : (language === 'es' ? 'Invitación rechazada correctamente' : 'Invitation rejected successfully')
                );
                
                // Recargar invitaciones pendientes
                loadPendingInvitations();
                
                // Si se aprobó, también recargar grupos
                if (approve) {
                    loadGroups();
                }
                
                // Ocultar mensaje de éxito después de 3 segundos
                setTimeout(() => {
                    setSuccess(null);
                }, 3000);
            } else {
                setError(response.data.message || (language === 'es'
                    ? 'Error al procesar la invitación'
                    : 'Error processing the invitation'));
            }
        } catch (err: any) {
            console.error('Error processing invitation:', err);
            
            setError(language === 'es'
                ? 'Error al procesar la invitación'
                : 'Error processing the invitation');
        } finally {
            setIsProcessingInvitation(false);
        }
    };

    // Salir de un grupo
    const handleLeaveGroup = async (groupId: string) => {
        setIsLeavingGroup(true);
        setError(null);
        setSuccess(null);
        
        try {
            const currentToken = getHatateToken();
            const response = await axios.post(
                `${API_BASE_URL}/leave-group`,
                { groupId },
                { 
                    headers: { 
                        'x-api-key': API_KEY,
                        'Authorization': `${currentToken}`
                    },
                    params: {
                        mlStuff: {
                            whatsAppServerPort: whatsAppServerPort,
                            whatsAppApiPort: whatsAppApiPort,
                            mlServerPort: mlServerPort
                        }
                    }
                }
            );
            
            if (response.data.success) {
                setSuccess(language === 'es'
                    ? 'Bot ha abandonado el grupo correctamente'
                    : 'Bot has left the group successfully');
                
                // Recargar grupos
                loadGroups();
                
                // Ocultar mensaje de éxito después de 3 segundos
                setTimeout(() => {
                    setSuccess(null);
                }, 3000);
            } else {
                setError(response.data.message || (language === 'es'
                    ? 'Error al abandonar el grupo'
                    : 'Error leaving the group'));
            }
        } catch (err: any) {
            console.error('Error leaving group:', err);
            
            setError(language === 'es'
                ? 'Error al abandonar el grupo'
                : 'Error leaving the group');
        } finally {
            setIsLeavingGroup(false);
        }
    };

    // Formatear la fecha para mostrarla
    const formatDate = (dateString: string) => {
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (e) {
            return dateString;
        }
    };

    // Acortar IDs para mostrarlos
    const shortenId = (id: string) => {
        if (!id) return '';
        if (id.includes('@')) {
            const parts = id.split('@');
            return parts[0].substring(0, 8) + '...@' + parts[1];
        }
        return id.substring(0, 12) + '...';
    };
    
    // Manejar cambio de pestaña
    const handleTabChange = (tab: string | null) => {
        if (tab) {
            setActiveTab(tab);
        }
    };
    
    return (
        <Card className="mb-4">
            <Card.Header className="bg-primary text-white">
                <FontAwesomeIcon icon={faUsers} className="me-2" />
                {language === 'es' ? 'Gestión de Grupos de WhatsApp' : 'WhatsApp Group Management'}
            </Card.Header>
            <Card.Body>
                {error && (
                    <Alert variant="danger" dismissible onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}
                
                {success && (
                    <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
                        {success}
                    </Alert>
                )}
                
                <Tabs defaultActiveKey="join" className="mb-3" onSelect={handleTabChange}>
                    {/* Pestaña para unirse a un grupo */}
                    <Tab eventKey="join" title={
                        <span>
                            <FontAwesomeIcon icon={faPlus} className="me-2" />
                            {language === 'es' ? 'Unirse a Grupo' : 'Join Group'}
                        </span>
                    }>
                        <Form onSubmit={handleSubmit}>
                            <Form.Group className="mb-3">
                                <Form.Label>
                                    {language === 'es' ? 'Código de invitación o enlace completo' : 'Invitation code or full link'}
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder={language === 'es' 
                                        ? 'Ej: AbCdEfGhIjKl1234 o https://chat.whatsapp.com/AbCdEfGhIjKl1234' 
                                        : 'E.g: AbCdEfGhIjKl1234 or https://chat.whatsapp.com/AbCdEfGhIjKl1234'}
                                    value={inviteCode}
                                    onChange={(e) => setInviteCode(e.target.value)}
                                    disabled={disabled || isSubmitting}
                                />
                                <Form.Text className="text-muted">
                                    {language === 'es'
                                        ? 'Introduce el código de invitación o el enlace completo de invitación al grupo'
                                        : 'Enter the invitation code or the full group invitation link'}
                                </Form.Text>
                            </Form.Group>
                            
                            <Button 
                                variant="success" 
                                type="submit" 
                                disabled={disabled || isSubmitting || !inviteCode.trim()}
                            >
                                {isSubmitting ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        {language === 'es' ? 'Uniéndose...' : 'Joining...'}
                                    </>
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faPlus} className="me-2" />
                                        {language === 'es' ? 'Unirse al Grupo' : 'Join Group'}
                                    </>
                                )}
                            </Button>
                        </Form>
                    </Tab>
                    
                    {/* Pestaña para invitaciones pendientes */}
                    <Tab eventKey="pending" title={
                        <span>
                            <FontAwesomeIcon icon={faBell} className="me-2" />
                            {language === 'es' ? 'Invitaciones Pendientes' : 'Pending Invitations'}
                            {pendingInvitations.length > 0 && (
                                <Badge bg="danger" className="ms-2">{pendingInvitations.length}</Badge>
                            )}
                        </span>
                    }>
                        <div className="d-flex justify-content-between mb-3">
                            <h5>
                                {language === 'es' 
                                    ? `Invitaciones pendientes (${pendingInvitations.length})` 
                                    : `Pending invitations (${pendingInvitations.length})`}
                            </h5>
                            {isLoadingInvitations && (
                                <Spinner animation="border" size="sm" />
                            )}
                        </div>
                        
                        {pendingInvitations.length > 0 ? (
                            <div className="table-responsive">
                                <Table striped hover>
                                    <thead>
                                        <tr>
                                            <th>{language === 'es' ? 'Fecha' : 'Date'}</th>
                                            <th>{language === 'es' ? 'Remitente' : 'Sender'}</th>
                                            <th>{language === 'es' ? 'Grupo' : 'Group'}</th>
                                            <th>{language === 'es' ? 'Código' : 'Code'}</th>
                                            <th>{language === 'es' ? 'Acciones' : 'Actions'}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pendingInvitations.map((invitation) => (
                                            <tr key={invitation.id}>
                                                <td>{formatDate(invitation.timestamp)}</td>
                                                <td>{shortenId(invitation.sender)}</td>
                                                <td>{invitation.groupName || (language === 'es' ? 'Desconocido' : 'Unknown')}</td>
                                                <td>{invitation.inviteCode.substring(0, 8)}...</td>
                                                <td>
                                                    <Button
                                                        variant="success"
                                                        size="sm"
                                                        className="me-2"
                                                        disabled={disabled || isProcessingInvitation}
                                                        onClick={() => handleProcessInvitation(invitation.id, true)}
                                                    >
                                                        <FontAwesomeIcon icon={faCheck} className="me-1" />
                                                        {language === 'es' ? 'Aprobar' : 'Approve'}
                                                    </Button>
                                                    <Button
                                                        variant="danger"
                                                        size="sm"
                                                        disabled={disabled || isProcessingInvitation}
                                                        onClick={() => handleProcessInvitation(invitation.id, false)}
                                                    >
                                                        <FontAwesomeIcon icon={faTimes} className="me-1" />
                                                        {language === 'es' ? 'Rechazar' : 'Reject'}
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        ) : (
                            <Alert variant="info">
                                <FontAwesomeIcon icon={faBell} className="me-2" />
                                {language === 'es' 
                                    ? 'No hay invitaciones pendientes' 
                                    : 'No pending invitations'}
                            </Alert>
                        )}
                    </Tab>
                    
                    {/* Pestaña para listado de grupos */}
                    <Tab eventKey="groups" title={
                        <span>
                            <FontAwesomeIcon icon={faUserGroup} className="me-2" />
                            {language === 'es' ? 'Grupos' : 'Groups'}
                            {groups.length > 0 && (
                                <Badge bg="info" className="ms-2">{groups.length}</Badge>
                            )}
                        </span>
                    }>
                        <div className="d-flex justify-content-between mb-3">
                            <h5>
                                {language === 'es' 
                                    ? `Grupos actuales (${groups.length})` 
                                    : `Current groups (${groups.length})`}
                            </h5>
                            {isLoadingGroups && (
                                <Spinner animation="border" size="sm" />
                            )}
                        </div>
                        
                        {groups.length > 0 ? (
                            <div className="table-responsive">
                                <Table striped hover>
                                    <thead>
                                        <tr>
                                            <th>{language === 'es' ? 'Nombre del Grupo' : 'Group Name'}</th>
                                            <th>{language === 'es' ? 'Participantes' : 'Participants'}</th>
                                            <th>{language === 'es' ? 'Última Actividad' : 'Last Activity'}</th>
                                            <th>{language === 'es' ? 'Estado' : 'Status'}</th>
                                            <th>{language === 'es' ? 'Acciones' : 'Actions'}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {groups.map((group) => (
                                            <tr key={group.id}>
                                                <td>{group.name}</td>
                                                <td>{group.participants}</td>
                                                <td>{formatDate(group.lastActivity)}</td>
                                                <td>
                                                    {group.isActive ? (
                                                        <Badge bg="success">
                                                            {language === 'es' ? 'Activo' : 'Active'}
                                                        </Badge>
                                                    ) : (
                                                        <Badge bg="secondary">
                                                            {language === 'es' ? 'Inactivo' : 'Inactive'}
                                                        </Badge>
                                                    )}
                                                </td>
                                                <td>
                                                    <Button
                                                        variant="danger"
                                                        size="sm"
                                                        disabled={disabled || isLeavingGroup}
                                                        onClick={() => handleLeaveGroup(group.id)}
                                                    >
                                                        <FontAwesomeIcon icon={faSignOutAlt} className="me-1" />
                                                        {language === 'es' ? 'Abandonar' : 'Leave'}
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        ) : (
                            <Alert variant="info">
                                <FontAwesomeIcon icon={faUserGroup} className="me-2" />
                                {language === 'es' 
                                    ? 'No hay grupos activos' 
                                    : 'No active groups'}
                            </Alert>
                        )}
                    </Tab>
                </Tabs>
            </Card.Body>
        </Card>
    );
};

export default JoinGroupComponent;